import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { ToastService } from '../../Service/toast-service';
import { ServiceOrder } from '../../Serviceorder/models/service-order'; 
import { ServiceOrderService } from '../../Service/service-order-service';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-serviceorder-edit',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, MatDialogModule],
  templateUrl: './serviceorder-edit.html',
  styleUrl: './serviceorder-edit.css',
})
export class ServiceorderEdit implements OnInit {
  public orderForm!: FormGroup;
  public listaMecanicos: any[] = []; // Almacenará la consulta de la UDC o F0101 de mecánicos
  public guardando: boolean = false;
  public cargandoMecanicos: boolean = true; 

  // Catálogo operativo indexado de estatus de rampa
  public catalogoEstatusTrabajo: string[] = ['ABIERTA', 'EN_RAMPA', 'PINTURA', 'TERMINADA'];

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    public toastService: ToastService,
    private dialogRef: MatDialogRef<ServiceorderEdit>,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    this.inicializarFormulario();
    this.cargarCatalogoMecanicos();
  }

  private inicializarFormulario(): void {
    // Mapeamos los campos reales herencia de tu entidad de Spring Boot
    this.orderForm = this.fb.group({
      coId: [this.data.coId],
      id: [this.data.id],
      idRecepcion: [this.data.idRecepcion],
      an8Cliente: [this.data.an8Cliente],
      an8Tecnico: [null, Validators.required], // Nace en null provisionalmente
      orderStatus: [this.data.orderStatus || 'PEND_ASIG'],     // Estatus General
      status: [this.data.status || 'ABIERTA', Validators.required], // Estatus de Línea/Rampa
      description: [this.data.description],
      orderAmount: [{ value: this.data.orderAmount, disabled: true }]
    });
  }

  private cargarCatalogoMecanicos(): void {
    this.cargandoMecanicos = true; // Iniciamos la carga activa
    console.log('📡 [SaaS Taller] Descargando padrón optimizado de mecánicos desde la API...');
    
    this.http.get<any[]>('/api/service-order/mecanicos').subscribe({
      next: (usuarios) => {
        // 🌟 MATCH DIRECTO: Tu API ya viene filtrada desde Java, asignamos de inmediato
        this.listaMecanicos = usuarios || [];
        this.cargandoMecanicos = false; // Finaliza la carga de red con éxito
        console.log(`✅ [SaaS Taller] Se cargaron ${this.listaMecanicos.length} mecánicos en el catálogo.`);

        // 🎯 ACOPLAMIENTO DE INTEGRIDAD SIN PARPADEOS:
        // Una vez que el select tiene sus options físicas en RAM, inyectamos el AN8 del responsable
        this.orderForm.patchValue({
          an8Tecnico: this.data.an8Tecnico
        }, { emitEvent: false });

        // 🛡️ REGLA DE NEGOCIO: Marcamos el formulario como inmutable/limpio de arranque
        // Esto congela el botón "Sellar Asignación" en azul tenue hasta que el usuario altere algo
        this.orderForm.markAsPristine();
        
        // Forzamos a Angular a esparcir el operario seleccionado en la interfaz
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('❌ Error en el catálogo de mecánicos:', err);
        this.cargandoMecanicos = false; // Apagamos el spinner de igual modo
        this.toastService.showDanger('Error', 'No se pudo descargar el padrón de mecánicos.');
        this.cdr.detectChanges();
      }
    });
  }

  // 🚀 MÉTODO UNIFICADO: Control del Reloj de Producción amarrado a las UDCs del Taller
  public cambiarEstatusRampa(nuevoEstado: 'EN_PROGRESO' | 'EN_RAMPA' | 'PINTURA' | 'TERMINADO'): void {
    console.log(`⏱️ [SaaS Reloj] Moviendo hito físico de rampa a: ${nuevoEstado}`);

    // Sincronizamos las dos columnas de forma coordinada
    this.orderForm.patchValue({
      status: 'ABIERTA',         // El estatus administrativo permanece ABIERTA para cobranza/firmas
      orderStatus: nuevoEstado   // Inserta exactamente 'EN_PROGRESO', 'EN_RAMPA', 'PINTURA' o 'TERMINADO'
    });
    
    // Forzamos a Angular a habilitar el botón de submit al detectar cambios en caliente
    this.orderForm.markAsDirty();
    
    this.toastService.showSuccess('Reloj Operativo', `Unidad conmutada con éxito a la fase: ${nuevoEstado}`);
    
    // Obligamos a la pantalla a redibujar los botones activos y los colores de la alerta superior
    this.cdr.detectChanges();
  }

  public guardarCambiosOS(): void {
    if (this.orderForm.invalid) {
      this.orderForm.markAllAsTouched();
      return;
    }

    this.guardando = true;
    
    // 🚀 SEGURO DE FLUJO: Si ya hay un técnico asignado y sigue en PEND_ASIG, lo movemos a EN_PROGRESO
    if (this.orderForm.get('an8Tecnico')?.value && this.orderForm.get('orderStatus')?.value === 'PEND_ASIG') {
      this.orderForm.get('orderStatus')?.setValue('EN_PROGRESO');
    }

    const payloadEdicion = this.orderForm.getRawValue();

    if (payloadEdicion.an8Tecnico) {
      payloadEdicion.an8Tecnico = Number(payloadEdicion.an8Tecnico);
    }

    console.log('📡 Despachando actualización operativa:', payloadEdicion);

    this.http.put(`/api/service-order/${payloadEdicion.id}/update-production`, payloadEdicion).subscribe({
      next: (res: any) => {
        this.toastService.showSuccess('Cambios Guardados', 'Técnico asignado y estatus de rampa congelados con éxito.');
        this.dialogRef.close(res);
      },
      error: (err) => {
        this.guardando = false;
        console.error(err);
        this.toastService.showDanger('Fallo de Persistencia', 'El servidor rechazó la actualización de la orden de servicio.');
      }
    });
  }

  public cerrarModal(): void {
    this.dialogRef.close(false);
  }
}

